import { ReactElement, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { AxiosResultDefaultError } from '../../../../api/request';
import { createFetchContext } from '../../../../components/contexts/FetchContextFactory';
import DisplayError from '../../../../components/info/DisplayError';
import Loading from '../../../../components/info/Loading';
import { TranslationsKeys } from '../../../../locales/translations';
import { getAuthorizationRequests } from '../api';
import { HomologationListFilter, HomologationListResponse } from '../types';

const { FetchProvider, FetchConsumer, useFetch } = createFetchContext<
  HomologationListFilter,
  HomologationListResponse,
  AxiosResultDefaultError
>();

export function useAuthorizationDataLoader() {
  return useFetch();
}

export default function AuthorizationDataLoader({
  children,
}: {
  children: ReactElement | ReactElement[];
}) {
  const request = useCallback(async (query: HomologationListFilter) => {
    return getAuthorizationRequests({
      ...query,
      page: query.page - 1,
    });
  }, []);

  return (
    <FetchProvider
      request={request}
      defaultQuery={{ page: 1, items_per_page: 10 }}
      fetchImmediately
    >
      {children}
    </FetchProvider>
  );
}

export function AuthorizationTableContainer({
  children,
}: {
  children: ReactElement;
}) {
  const { t } = useTranslation(TranslationsKeys.HOMOLOGATION_AUTHORIZATION);

  return (
    <FetchConsumer>
      {({ loading, data, error, refresh }) => {
        if (loading) {
          return <Loading insideCard />;
        } else if (error) {
          return (
            <DisplayError
              insideCard
              title={t('error.title')}
              textBody={t('error.body')}
              retryAction={refresh}
            />
          );
        } else if (data) {
          return <>{children}</>;
        }
        return null;
      }}
    </FetchConsumer>
  );
}
